import {
  CostValuationSource,
  PROVIDER_CONSUMPTION_ORIGINS,
  PROVIDER_CONSUMPTION_SORT_KEYS,
  ProviderConsumption,
  ProviderConsumptionOrigin,
  ProviderConsumptionsFilter,
  ProviderConsumptionSortKey,
  ProviderConsumptionStatus,
  ProviderSyncAlarmsFilter,
  ProviderSyncRun,
  ProviderSyncStatus,
  ProviderToSettleFilter,
  ProviderToSettleRejection,
  ProviderToSettleResult,
  PROVIDER_TO_SETTLE_BATCH_SIZE,
  PROVIDER_TO_SETTLE_FILTER_MAX,
  SortDirection,
  UndoImportTarget,
} from '../models/Entities/ProviderConsumptions/ProviderConsumptions';
import { ImportAssignmentStatus, ProviderImportJob } from '../models/Entities/ProviderConsumptionImport/ProviderConsumptionImport';
import { StatusTone } from '../theme/guideTokens';
import { assignmentStatusOf, confidencePercent, formatImportAmount, formatImportDate } from './ProviderConsumptionImport.Functions';
import { dateCell, ExcelRow, ExcelSheet, integer, money, percent } from '../components/Contracts/statistics/StatisticsExcel.Functions';

/**
 * Grilla unificada de consumos del proveedor (spec 006, MP 028): lógica pura (filtros → query,
 * etiquetas, valorización, alarmas, polling, deep-link y hoja de Excel). Sin efectos.
 */

/** Traductor (i18next `t`) para las funciones que arman textos. */
export type Translate = (key: string, options?: Record<string, unknown>) => string;

const NS = 'providerConsumptions';
const key = (path: string) => `${NS}.${path}`;

// ---------------------------------------------------------------------------------------------
// Filtros → query
// ---------------------------------------------------------------------------------------------

export const DEFAULT_SORT: { sortBy: ProviderConsumptionSortKey; sortDirection: SortDirection } = {
  sortBy: 'serviceDueDate',
  sortDirection: 'desc',
};

export const PROVIDER_CONSUMPTIONS_PAGE_SIZES = [25, 50, 100];
export const DEFAULT_PROVIDER_CONSUMPTIONS_PAGE_SIZE = 25;

/** Filtro inicial (sin filtros, primera página, más recientes primero). */
export const initialProviderConsumptionsFilter = (origins: number[] = []): ProviderConsumptionsFilter => ({
  searchText: '',
  origins,
  protocolId: undefined,
  status: undefined,
  hasAlarm: false,
  dateFrom: undefined,
  dateTo: undefined,
  providerFileId: undefined,
  assignmentStatus: undefined,
  pageNumber: 1,
  pageSize: DEFAULT_PROVIDER_CONSUMPTIONS_PAGE_SIZE,
  ...DEFAULT_SORT,
});

/** El filtro incluye el origen "importado de archivo del proveedor" (5): habilita archivo y estado de asignación. */
export const includesProviderFileImport = (origins: number[] | undefined): boolean =>
  (origins ?? []).includes(ProviderConsumptionOrigin.ProviderFileImport);

/** Quita los filtros que sólo valen con origen 5 cuando ya no está elegido. */
export const normalizeOriginDependentFilters = (filter: ProviderConsumptionsFilter): ProviderConsumptionsFilter =>
  includesProviderFileImport(filter.origins) ? filter : { ...filter, providerFileId: undefined, assignmentStatus: undefined };

/** `DateFrom` posterior a `DateTo` (la API responde 400). */
export const isDateRangeInvalid = (from: string | undefined, to: string | undefined): boolean => !!from && !!to && from > to;

/** Hay algún filtro aplicado (para el estado vacío y "Limpiar filtros"). */
export const hasActiveFilters = (filter: ProviderConsumptionsFilter): boolean =>
  !!filter.searchText?.trim() ||
  (filter.origins?.length ?? 0) > 0 ||
  filter.protocolId !== undefined ||
  filter.status !== undefined ||
  !!filter.hasAlarm ||
  !!filter.dateFrom ||
  !!filter.dateTo ||
  !!filter.providerFileId ||
  !!filter.assignmentStatus;

/**
 * Cuántos filtros plegables están aplicados (sin contar la búsqueda, que siempre está a la vista):
 * origen, protocolo, estado, fechas (desde y hasta cuentan como uno), archivo, asignación y alarma.
 */
export const activeFilterCount = (filter: ProviderConsumptionsFilter): number =>
  [
    (filter.origins?.length ?? 0) > 0,
    filter.protocolId !== undefined,
    filter.status !== undefined,
    !!filter.dateFrom || !!filter.dateTo,
    !!filter.providerFileId,
    !!filter.assignmentStatus,
    !!filter.hasAlarm,
  ].filter(Boolean).length;

/**
 * Query de la lista (`paged: true`, con paginado y orden) o de los totales / exportación (`paged: false`).
 * `Origins` se repite por cada origen; archivo y estado de asignación sólo viajan con origen 5.
 */
export const buildProviderConsumptionsQuery = (filter: ProviderConsumptionsFilter, { paged }: { paged: boolean }): string => {
  const params = new URLSearchParams();
  const normalized = normalizeOriginDependentFilters(filter);
  if (paged) {
    params.append('PageNumber', String(normalized.pageNumber));
    params.append('PageSize', String(normalized.pageSize));
  }
  const search = normalized.searchText?.trim();
  if (search) params.append('SearchText', search.slice(0, 200));
  Array.from(new Set(normalized.origins ?? [])).forEach((origin) => params.append('Origins', String(origin)));
  if (normalized.protocolId !== undefined) params.append('ProtocolId', String(normalized.protocolId));
  if (normalized.status !== undefined) params.append('Status', String(normalized.status));
  if (normalized.hasAlarm) params.append('HasAlarm', 'true');
  if (normalized.dateFrom) params.append('DateFrom', normalized.dateFrom);
  if (normalized.dateTo) params.append('DateTo', normalized.dateTo);
  if (normalized.providerFileId) params.append('ProviderFileId', normalized.providerFileId);
  if (normalized.assignmentStatus) params.append('AssignmentStatus', normalized.assignmentStatus);
  if (paged) {
    const sortBy = isSortKey(normalized.sortBy) ? normalized.sortBy : DEFAULT_SORT.sortBy;
    params.append('SortBy', sortBy);
    params.append('SortDirection', normalized.sortDirection === 'asc' ? 'asc' : 'desc');
  }
  const text = params.toString();
  return text ? `?${text}` : '';
};

/** Query de las alarmas de una corrida. */
export const buildSyncAlarmsQuery = (filter: ProviderSyncAlarmsFilter): string => {
  const params = new URLSearchParams();
  if (filter.reason !== undefined) params.append('reason', String(filter.reason));
  if (filter.protocolId !== undefined) params.append('protocolId', String(filter.protocolId));
  params.append('pageNumber', String(filter.pageNumber));
  params.append('pageSize', String(Math.min(500, Math.max(1, filter.pageSize))));
  return `?${params.toString()}`;
};

export const isSortKey = (value: unknown): value is ProviderConsumptionSortKey =>
  typeof value === 'string' && (PROVIDER_CONSUMPTION_SORT_KEYS as readonly string[]).includes(value);

/** Click en un encabezado: misma columna → alterna; otra → ascendente. Siempre vuelve a la página 1. */
export const nextSort = (filter: ProviderConsumptionsFilter, sortBy: ProviderConsumptionSortKey): ProviderConsumptionsFilter => ({
  ...filter,
  sortBy,
  sortDirection: filter.sortBy === sortBy ? (filter.sortDirection === 'asc' ? 'desc' : 'asc') : 'asc',
  pageNumber: 1,
});

// ---------------------------------------------------------------------------------------------
// Etiquetas y tonos
// ---------------------------------------------------------------------------------------------

const ORIGIN_TONES: Record<number, StatusTone> = {
  [ProviderConsumptionOrigin.SyncProcess]: 'info',
  [ProviderConsumptionOrigin.Manual]: 'neutral',
  [ProviderConsumptionOrigin.FileImport]: 'brand',
  [ProviderConsumptionOrigin.ExternalInformed]: 'brand',
  [ProviderConsumptionOrigin.ProviderFileImport]: 'confirmed',
};

export const originLabelKey = (origin: number | null | undefined): string =>
  origin !== null && origin !== undefined && PROVIDER_CONSUMPTION_ORIGINS.includes(origin) ? key(`origin.${origin}`) : key('origin.other');

export const originTone = (origin: number | null | undefined): StatusTone => (origin ? ORIGIN_TONES[origin] ?? 'neutral' : 'neutral');

const STATUS_TONES: Record<number, StatusTone> = {
  0: 'warning',
  1: 'warning',
  2: 'success',
  3: 'brand',
  4: 'danger',
  5: 'neutral',
  6: 'info',
  7: 'confirmed',
  8: 'info',
};

export const consumptionStatusLabelKey = (status: number | null | undefined): string =>
  status !== null && status !== undefined && STATUS_TONES[status] !== undefined ? key(`status.${status}`) : key('status.unknown');

export const consumptionStatusTone = (status: number | null | undefined): StatusTone =>
  status !== null && status !== undefined ? STATUS_TONES[status] ?? 'neutral' : 'neutral';

const SYNC_STATUS_TONES: Record<number, StatusTone> = {
  [ProviderSyncStatus.Registered]: 'neutral',
  [ProviderSyncStatus.InProgress]: 'info',
  [ProviderSyncStatus.Finished]: 'success',
  [ProviderSyncStatus.Cancelled]: 'neutral',
  [ProviderSyncStatus.Fail]: 'danger',
  [ProviderSyncStatus.PartiallyFailed]: 'warning',
};

export const syncStatusLabelKey = (status: number | null | undefined): string =>
  status && SYNC_STATUS_TONES[status] ? key(`sync.status.${status}`) : key('sync.status.unknown');

export const syncStatusTone = (status: number | null | undefined): StatusTone => (status ? SYNC_STATUS_TONES[status] ?? 'neutral' : 'neutral');

export const syncTriggerLabelKey = (trigger: number | null | undefined): string =>
  trigger && trigger >= 1 && trigger <= 3 ? key(`sync.trigger.${trigger}`) : key('sync.trigger.unknown');

export const alarmReasonLabelKey = (reason: number | null | undefined): string =>
  reason && reason >= 1 && reason <= 14 ? key(`alarmReason.${reason}`) : key('alarmReason.unknown');

export const appliedPriceTypeLabelKey = (type: number | null | undefined): string =>
  type && type >= 1 && type <= 5 ? key(`priceType.${type}`) : key('priceType.unknown');

/** Alcance de una corrida: un protocolo o todos (ventana de 6 meses desde `windowStartDate`). */
export const syncScopeText = (run: Pick<ProviderSyncRun, 'protocolId' | 'windowStartDate'>, t: Translate): string => {
  if (run.protocolId) return t(key('sync.scopeProtocol'), { protocolId: run.protocolId });
  return run.windowStartDate
    ? t(key('sync.scopeAllSince'), { date: formatImportDate(run.windowStartDate) })
    : t(key('sync.scopeAll'));
};

const pad = (value: number) => String(value).padStart(2, '0');

/** Fecha y hora local (dd/mm/aaaa hh:mm) de una fecha ISO (UTC); "—" si no hay. */
export const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/** Monto con moneda ("ARS 1.234,50"); "—" si no hay. */
export const formatMoney = (amount: number | null | undefined, currency: string | null | undefined, locale = 'es-AR'): string =>
  formatImportAmount(amount, currency, locale);

/** Porcentaje legible ("30", "12,5"). */
export const formatPercentage = (value: number | null | undefined, locale = 'es-AR'): string =>
  value === null || value === undefined || Number.isNaN(Number(value)) ? '—' : Number(value).toLocaleString(locale, { maximumFractionDigits: 2 });

// ---------------------------------------------------------------------------------------------
// Valorización, alarma, asignación y vínculo
// ---------------------------------------------------------------------------------------------

export interface ValuationText {
  /** Fuente de la valorización (1 / 2 / 3) o undefined si no está valorizado. */
  source: CostValuationSource | undefined;
  label: string;
  tooltip: string;
}

/**
 * De dónde sale el costo, con texto corto para la celda y el detalle para el tooltip:
 * lista de precios (#id, vigencia y tipo de precio), costo fijo del concepto o "X % de {base}".
 */
export const describeValuation = (item: ProviderConsumption, t: Translate): ValuationText => {
  switch (item.costValuationSource) {
    case CostValuationSource.FixedConceptCost:
      return { source: CostValuationSource.FixedConceptCost, label: t(key('valuation.fixed')), tooltip: t(key('valuation.fixedTooltip')) };
    case CostValuationSource.ProviderPriceList: {
      const priceList = item.priceListId ? t(key('valuation.priceListId'), { id: item.priceListId }) : t(key('valuation.priceList'));
      const to = item.priceListEffectiveDateEnd ? formatImportDate(item.priceListEffectiveDateEnd) : t(key('valuation.noEndDate'));
      const parts = [priceList];
      if (item.priceListEffectiveDateStart) parts.push(t(key('valuation.effective'), { from: formatImportDate(item.priceListEffectiveDateStart), to }));
      if (item.appliedPriceType) parts.push(t(appliedPriceTypeLabelKey(item.appliedPriceType)));
      return { source: CostValuationSource.ProviderPriceList, label: t(key('valuation.priceList')), tooltip: parts.join(' · ') };
    }
    case CostValuationSource.ImportedValue: {
      const original = item.importAmount !== null && item.importAmount !== undefined ? formatMoney(item.importAmount, item.importCurrencyCode || item.currencyCode) : undefined;
      const tooltip = original ? `${t(key('valuation.fileTooltip'))} · ${original}` : t(key('valuation.fileTooltip'));
      return { source: CostValuationSource.ImportedValue, label: t(key('valuation.file')), tooltip };
    }
    case CostValuationSource.ConceptPercentageCost: {
      const params = {
        percentage: formatPercentage(item.costPercentage),
        base: formatMoney(item.costBaseUnitAmount, item.currencyCode),
      };
      return {
        source: CostValuationSource.ConceptPercentageCost,
        label: t(key('valuation.percentage'), params),
        tooltip: t(key('valuation.percentageTooltip'), params),
      };
    }
    default:
      return { source: undefined, label: t(key('valuation.none')), tooltip: t(key('valuation.noneTooltip')) };
  }
};

export interface AlarmText {
  /** Texto principal (motivo en español de la API o comentario de la alerta). */
  text?: string;
  /** Detalle técnico (settlementAlarmDetail). */
  detail?: string;
}

/** La fila tiene alarma: motivo de valorización o alerta del consumo (misma regla que `HasAlarm`). */
export const consumptionHasAlarm = (item: ProviderConsumption): boolean =>
  !!item.hasAlarm ||
  (item.settlementAlarmReason !== null && item.settlementAlarmReason !== undefined) ||
  !!item.settlementAlarmReasonDescription?.trim() ||
  !!item.markedAsHaveWarnings;

/** Textos de la alarma: `settlementAlarmReasonDescription` + detalle, o `warningsComments`. */
export const describeAlarm = (item: ProviderConsumption): AlarmText | undefined => {
  if (!consumptionHasAlarm(item)) return undefined;
  const description = item.settlementAlarmReasonDescription?.trim();
  const detail = item.settlementAlarmDetail?.trim();
  const comment = item.warningsComments?.trim();
  if (description) return { text: description, detail: detail && detail !== description ? detail : comment || undefined };
  if (comment) return { text: comment, detail: detail || undefined };
  return { text: undefined, detail: detail || undefined };
};

/** Sólo los consumos importados de archivo del proveedor admiten las acciones de la spec 005. */
export const isProviderFileImport = (item: Pick<ProviderConsumption, 'origin'>): boolean =>
  Number(item.origin) === ProviderConsumptionOrigin.ProviderFileImport;

/** Estado de asignación (sólo origen 5): el de la API o, si no viene, el calculado (spec 005). */
export const providerAssignmentStatus = (item: ProviderConsumption): ImportAssignmentStatus | undefined => {
  if (!isProviderFileImport(item)) return undefined;
  if (item.assignmentStatus === 'Unassigned' || item.assignmentStatus === 'Assigned' || item.assignmentStatus === 'Generated') {
    return item.assignmentStatus;
  }
  return assignmentStatusOf(item);
};

/** Ids seleccionados de origen 5 que cumplen la condición. */
const selectedWhere = (items: ProviderConsumption[], selected: string[], predicate: (status: ImportAssignmentStatus | undefined) => boolean) => {
  const wanted = new Set(selected);
  return items.filter((item) => wanted.has(item.id) && isProviderFileImport(item) && predicate(providerAssignmentStatus(item))).map((item) => item.id);
};

/** Seleccionados que se pueden (re)asignar: origen 5 y no generados. */
export const selectedAssignableIds = (items: ProviderConsumption[], selected: string[]): string[] =>
  selectedWhere(items, selected, (status) => status !== 'Generated');

/** Seleccionados que se pueden generar: origen 5 asignados y no generados. */
export const selectedGeneratableIds = (items: ProviderConsumption[], selected: string[]): string[] =>
  selectedWhere(items, selected, (status) => status === 'Assigned');

// ---------------------------------------------------------------------------------------------
// Marcado "Por liquidar" (spec 010, MP 032)
// ---------------------------------------------------------------------------------------------

/** D-2, sólo como ayuda visual (contador de elegibles): la decisión es del servidor. */
export const canMarkToSettle = (item: ProviderConsumption): boolean =>
  item.status !== null &&
  item.status !== undefined &&
  Number(item.status) === ProviderConsumptionStatus.NotSettled &&
  Number(item.amount ?? 0) > 0 &&
  !consumptionHasAlarm(item);

export const canUnmarkToSettle = (item: ProviderConsumption): boolean =>
  item.status !== null && item.status !== undefined && Number(item.status) === ProviderConsumptionStatus.ToSettle;

const selectedThat = (items: ProviderConsumption[], selected: string[], predicate: (item: ProviderConsumption) => boolean): string[] => {
  const wanted = new Set(selected);
  return items.filter((item) => wanted.has(item.id) && predicate(item)).map((item) => item.id);
};

/** Seleccionados (de cualquier origen) que el front estima marcables. */
export const selectedMarkableIds = (items: ProviderConsumption[], selected: string[]): string[] => selectedThat(items, selected, canMarkToSettle);

/** Seleccionados en "Por liquidar". */
export const selectedUnmarkableIds = (items: ProviderConsumption[], selected: string[]): string[] => selectedThat(items, selected, canUnmarkToSettle);

const ASSIGNMENT_STATUS_VALUES: Record<ImportAssignmentStatus, number> = { Unassigned: 1, Assigned: 2, Generated: 3 };

/**
 * Filtro del PATCH por filtro a partir del de la grilla: los mismos filtros que la query (archivo y asignación
 * sólo con origen 5), sin paginado ni orden ni vacíos. `hasAlarm` sólo como `true` (DIS-15) y `assignmentStatus`
 * como número (DIS-16).
 */
export const toSettleFilterOf = (filter: ProviderConsumptionsFilter): ProviderToSettleFilter => {
  const normalized = normalizeOriginDependentFilters(filter);
  const result: ProviderToSettleFilter = {};
  const search = normalized.searchText?.trim();
  if (search) result.searchText = search.slice(0, 200);
  const origins = Array.from(new Set(normalized.origins ?? []));
  if (origins.length > 0) result.origins = origins;
  if (normalized.protocolId !== undefined) result.protocolId = normalized.protocolId;
  if (normalized.status !== undefined) result.status = normalized.status;
  if (normalized.hasAlarm) result.hasAlarm = true;
  if (normalized.dateFrom) result.dateFrom = normalized.dateFrom;
  if (normalized.dateTo) result.dateTo = normalized.dateTo;
  if (normalized.providerFileId) result.providerFileId = normalized.providerFileId;
  if (normalized.assignmentStatus) result.assignmentStatus = ASSIGNMENT_STATUS_VALUES[normalized.assignmentStatus];
  return result;
};

/** "Todos los filtrados" no se puede usar: ninguno o más de 5000 (la API responde 400). */
export const filteredToSettleBlockReason = (total: number): 'empty' | 'tooMany' | undefined => {
  if (total <= 0) return 'empty';
  if (total > PROVIDER_TO_SETTLE_FILTER_MAX) return 'tooMany';
  return undefined;
};

/** Parte los ids en lotes (por defecto de 1000, el máximo del PATCH). */
export const chunkIds = (ids: string[], size = PROVIDER_TO_SETTLE_BATCH_SIZE): string[][] => {
  const chunks: string[][] = [];
  for (let index = 0; index < ids.length; index += size) chunks.push(ids.slice(index, index + size));
  return chunks;
};

/** Junta los resultados de varios lotes. */
export const mergeToSettleResults = (results: ProviderToSettleResult[]): ProviderToSettleResult =>
  results.reduce<ProviderToSettleResult>(
    (acc, result) => ({
      updated: acc.updated + Number(result.updated ?? 0),
      rejected: [...acc.rejected, ...(result.rejected ?? [])],
      rejectedTotal: acc.rejectedTotal + Number(result.rejectedTotal ?? 0),
    }),
    { updated: 0, rejected: [], rejectedTotal: 0 }
  );

export interface RejectionRow {
  id: string;
  service: string;
  date: string;
  message: string;
}

/** Filas del aviso de rechazados: servicio y fecha del ítem cargado (o el id si no está en la página) y el mensaje. */
export const describeRejections = (rejected: ProviderToSettleRejection[], items: ProviderConsumption[]): RejectionRow[] => {
  const byId = new Map(items.map((item) => [item.id, item]));
  return rejected.map((rejection) => {
    const item = byId.get(rejection.id);
    return {
      id: rejection.id,
      service: item ? item.serviceName || item.medicalServiceName || rejection.id : rejection.id,
      date: item?.serviceDueDate ? formatImportDate(item.serviceDueDate) : '',
      message: rejection.message?.trim() || rejection.reason,
    };
  });
};

/** Enlace a la liquidación del consumo (US5), o undefined. */
export const consumptionSettlementLink = (
  item: Pick<ProviderConsumption, 'settlementId' | 'settlementName'>
): { settlementId: number; label: string | undefined } | undefined =>
  item.settlementId ? { settlementId: Number(item.settlementId), label: item.settlementName?.trim() || undefined } : undefined;

// ---------------------------------------------------------------------------------------------
// Deshacer importación (spec 010, US7)
// ---------------------------------------------------------------------------------------------

/**
 * Cantidad para el diálogo desde la grilla: el total sólo si el filtro es por archivo (con sus orígenes) y no hay
 * otro filtro activo; si no, el total no es el del archivo completo y no se muestra.
 */
export const undoImportCountOf = (filter: ProviderConsumptionsFilter, total: number): number | undefined => {
  const normalized = normalizeOriginDependentFilters(filter);
  if (!normalized.providerFileId) return undefined;
  const others = hasActiveFilters({ ...normalized, origins: [], providerFileId: undefined });
  return others ? undefined : total;
};

/** Archivo a deshacer desde un job del historial: su archivo y los creados como cantidad. */
export const undoTargetOfJob = (job: Pick<ProviderImportJob, 'providerFileId' | 'fileName' | 'result'>): UndoImportTarget => ({
  fileId: String(job.providerFileId),
  fileName: job.fileName?.trim() || '—',
  count: job.result?.created ?? undefined,
});

/** Consumo de protocolo relacionado (sincronizado: `sourceProtocolConsumptionId`; importado generado: `protocolConsumedServiceId`). */
export const protocolConsumptionLink = (
  item: Pick<ProviderConsumption, 'protocolId' | 'sourceProtocolConsumptionId' | 'protocolConsumedServiceId'>
): { protocolId: number; consumptionId: string } | undefined => {
  const consumptionId = (item.sourceProtocolConsumptionId || item.protocolConsumedServiceId || '').toString().trim();
  if (!consumptionId || !item.protocolId) return undefined;
  return { protocolId: item.protocolId, consumptionId };
};

// ---------------------------------------------------------------------------------------------
// Detalle del consumo en un drawer (spec 010, US8)
// ---------------------------------------------------------------------------------------------

/** Acciones del pie del drawer de detalle que valen para el consumo. */
export interface ProviderConsumptionDetailActions {
  /** Origen 5 no generado (mismo criterio que `selectedAssignableIds`). */
  assign: boolean;
  mark: boolean;
  unmark: boolean;
  settlement: boolean;
  protocolConsumption: boolean;
}

export const providerConsumptionDetailActions = (item: ProviderConsumption): ProviderConsumptionDetailActions => {
  const assignment = providerAssignmentStatus(item);
  return {
    assign: assignment !== undefined && assignment !== 'Generated',
    mark: canMarkToSettle(item),
    unmark: canUnmarkToSettle(item),
    settlement: !!consumptionSettlementLink(item),
    protocolConsumption: !!protocolConsumptionLink(item),
  };
};

const VALUATION_SOURCES: number[] = [
  CostValuationSource.FixedConceptCost,
  CostValuationSource.ProviderPriceList,
  CostValuationSource.ConceptPercentageCost,
  CostValuationSource.ImportedValue,
];

/** Clave i18n del origen del costo: archivo, lista de precios, costo fijo, porcentaje o sin valorizar. */
export const valuationSourceLabelKey = (source: number | null | undefined): string =>
  source !== null && source !== undefined && VALUATION_SOURCES.includes(Number(source))
    ? key(`detail.valuationSource.${Number(source)}`)
    : key('detail.valuationSource.none');

/** Hay conversión de moneda: la original existe y es distinta de la del consumo. */
export const hasCurrencyConversion = (item: Pick<ProviderConsumption, 'currencyCode' | 'originalCurrencyCode'>): boolean => {
  const original = (item.originalCurrencyCode ?? '').trim().toUpperCase();
  return original !== '' && original !== (item.currencyCode ?? '').trim().toUpperCase();
};

/** "id - nombre" del protocolo de la fila; "" si no tiene. */
export const rowProtocolLabel = (item: Pick<ProviderConsumption, 'protocolId' | 'protocolName'>): string =>
  item.protocolId ? `${item.protocolId} - ${item.protocolName ?? ''}`.trim().replace(/\s-$/, '') : '';

// ---------------------------------------------------------------------------------------------
// Polling de la sincronización
// ---------------------------------------------------------------------------------------------

/** Cada cuánto se consulta `sync/last` mientras hay una corrida activa. */
export const SYNC_POLL_INTERVAL_MS = 5000;

/** La corrida sigue en cola o en curso. */
export const isSyncActive = (run: Pick<ProviderSyncRun, 'isActive' | 'status'> | null | undefined): boolean =>
  !!run && (run.isActive === true || ((run.isActive === null || run.isActive === undefined) && (run.status === ProviderSyncStatus.Registered || run.status === ProviderSyncStatus.InProgress)));

/** Hay que seguir consultando: se pidió seguir (409 / recién pedida) o la última corrida está activa. */
export const shouldPoll = (run: ProviderSyncRun | null | undefined, waiting = false): boolean => waiting || isSyncActive(run);

/** La corrida que se seguía terminó: antes activa (o esperada) y ahora no. */
export const syncJustFinished = (previous: ProviderSyncRun | null | undefined, next: ProviderSyncRun | null | undefined, waiting = false): boolean =>
  (waiting || isSyncActive(previous)) && !!next && !isSyncActive(next);

// ---------------------------------------------------------------------------------------------
// Deep-link de la solapa
// ---------------------------------------------------------------------------------------------

/** Solapas del detalle del proveedor. `importedConsumptions` (spec 005) redirige a `consumptions` + origen 5. */
export const PROVIDER_DETAIL_TAB_KEYS = ['consumptions', 'contact', 'financial', 'medicalService', 'priceLists'] as const;
export type ProviderDetailTabKey = (typeof PROVIDER_DETAIL_TAB_KEYS)[number];
export const LEGACY_IMPORTED_CONSUMPTIONS_TAB = 'importedConsumptions';

/** "1,5" → [1, 5] (sólo orígenes válidos, sin repetir). */
export const parseOrigins = (value: string | null | undefined): number[] =>
  Array.from(
    new Set(
      String(value ?? '')
        .split(',')
        .map((part) => Number(part.trim()))
        .filter((origin) => PROVIDER_CONSUMPTION_ORIGINS.includes(origin))
    )
  );

export interface ResolvedProviderDetailTab {
  tab: ProviderDetailTabKey;
  /** Orígenes iniciales del filtro de la grilla (de `?origins=` o del redirect). */
  origins: number[];
  /** El `?tab=` era el viejo `importedConsumptions`: hay que reescribir la URL. */
  redirected: boolean;
  /** `?providerFileId=` (sólo con origen 5): archivo inicial del filtro ("Ver consumos" del aviso, spec 008). */
  providerFileId?: string;
  /** `?importFileId=`: abrir el asistente de importación con ese archivo ("Reintentar" del aviso, spec 008). */
  importFileId?: string;
}

/**
 * Lee `?tab=`, `?origins=`, `?providerFileId=` y `?importFileId=`; `importedConsumptions` → `consumptions`
 * filtrado por origen 5.
 */
export const resolveProviderDetailTab = (search: string | null | undefined): ResolvedProviderDetailTab => {
  const params = new URLSearchParams(search ?? '');
  const tab = params.get('tab');
  if (tab === LEGACY_IMPORTED_CONSUMPTIONS_TAB) {
    return { tab: 'consumptions', origins: [ProviderConsumptionOrigin.ProviderFileImport], redirected: true };
  }
  const known = tab && (PROVIDER_DETAIL_TAB_KEYS as readonly string[]).includes(tab) ? (tab as ProviderDetailTabKey) : 'consumptions';
  const origins = known === 'consumptions' ? parseOrigins(params.get('origins')) : [];
  const providerFileId = known === 'consumptions' && includesProviderFileImport(origins) ? params.get('providerFileId')?.trim() || undefined : undefined;
  const importFileId = known === 'consumptions' ? params.get('importFileId')?.trim() || undefined : undefined;
  return { tab: known, origins, redirected: false, providerFileId, importFileId };
};

/** Query de la URL para una solapa (conserva otros parámetros; `origins` sólo en "Consumos"). */
export const buildProviderDetailSearch = (currentSearch: string | null | undefined, tab: ProviderDetailTabKey, origins: number[] = []): string => {
  const params = new URLSearchParams(currentSearch ?? '');
  params.set('tab', tab);
  if (tab === 'consumptions' && origins.length > 0) params.set('origins', origins.join(','));
  else params.delete('origins');
  // el archivo del filtro sólo vale con origen 5; el pedido de abrir el asistente se usa una sola vez
  if (tab !== 'consumptions' || !includesProviderFileImport(origins)) params.delete('providerFileId');
  params.delete('importFileId');
  return `?${params.toString()}`;
};

// ---------------------------------------------------------------------------------------------
// Excel
// ---------------------------------------------------------------------------------------------

/** Tope de páginas al exportar (500 por página → 100.000 consumos). */
export const EXPORT_PAGE_SIZE = 500;
export const EXPORT_MAX_PAGES = 200;

const EXCEL_COLUMNS = [
  'date', 'code', 'service', 'protocol', 'patient', 'medicalRecord', 'concept', 'origin', 'status', 'assignment', 'settlement',
  'quantity', 'unitAmount', 'amount', 'currency', 'valuation', 'alarm', 'importAmount', 'importCurrency',
  'patientReference', 'sourceReference', 'importConfidence',
];

/** Liquidación del consumo (spec 010 US5): el nombre, "Liquidación #id" sin nombre o vacío si no está liquidado. */
const settlementCell = (item: ProviderConsumption, t: Translate): string => {
  const settlement = consumptionSettlementLink(item);
  if (!settlement) return '';
  return settlement.label ?? t(key('grid.settlementLinkFallback'), { id: settlement.settlementId });
};

/** Una fila de Excel por consumo, con los mismos textos que la grilla. */
export const buildProviderConsumptionsSheet = (items: ProviderConsumption[], t: Translate): ExcelSheet => {
  const column = (name: string) => t(key(`excel.${name}`));
  const rows: ExcelRow[] = items.map((item) => {
    const alarm = describeAlarm(item);
    const assignment = providerAssignmentStatus(item);
    return {
      [column('date')]: dateCell(item.serviceDueDate),
      [column('code')]: item.medicalServiceCode ?? '',
      [column('service')]: item.serviceName || item.medicalServiceName || '',
      [column('protocol')]: rowProtocolLabel(item),
      [column('patient')]: item.patientNumberInProtocol ?? '',
      [column('medicalRecord')]: item.medicalRecordNumber ?? '',
      [column('concept')]: item.conceptDescription ?? '',
      [column('origin')]: t(originLabelKey(item.origin)),
      [column('status')]: t(consumptionStatusLabelKey(item.status)),
      [column('assignment')]: assignment ? t(`providerConsumptionImport.status.${assignment}`) : '',
      [column('settlement')]: settlementCell(item, t),
      [column('quantity')]: integer(item.quantity),
      [column('unitAmount')]: money(item.unitAmount),
      [column('amount')]: money(item.amount),
      [column('currency')]: item.currencyCode ?? '',
      [column('valuation')]: describeValuation(item, t).tooltip,
      [column('alarm')]: alarm ? [alarm.text, alarm.detail].filter(Boolean).join(' · ') || t(key('alarm.generic')) : '',
      [column('importAmount')]: money(item.importAmount),
      [column('importCurrency')]: item.importCurrencyCode ?? '',
      [column('patientReference')]: item.patientReference ?? '',
      [column('sourceReference')]: item.sourceReference ?? '',
      [column('importConfidence')]: percent(confidencePercent(item.importConfidence)),
    };
  });
  return { name: t(key('excel.sheetName')), rows, headers: EXCEL_COLUMNS.map(column), description: t(key('excel.description')) };
};

// ---------------------------------------------------------------------------------------------
// Grilla con scroll propio y vista ampliada (spec 008)
// ---------------------------------------------------------------------------------------------

export const GRID_FULLSCREEN_STORAGE_KEY = 'prebiller.providerConsumptions.fullscreen';

/** Vista ampliada recordada en la sesión (sessionStorage con try/catch). */
export const loadGridFullscreen = (): boolean => {
  try {
    return window.sessionStorage.getItem(GRID_FULLSCREEN_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

export const saveGridFullscreen = (value: boolean): void => {
  try {
    window.sessionStorage.setItem(GRID_FULLSCREEN_STORAGE_KEY, value ? 'true' : 'false');
  } catch {
    // sólo para esta vista
  }
};
